<template>
  <el-dialog
    title="Add Activities"
    :visible.sync="show"
    @close="resetModal(true)"
    :close-on-click-modal="false"
  >
    <el-row style="margin-bottom: 10px">
      <el-col style="display: flex; justify-content: space-between">
        <el-radio-group v-model="type" @change="checkValid = false">
          <el-radio label="Task">DataMaestro Tasks</el-radio>
          <el-radio label="Process">TurboIntegrator Processes</el-radio>
        </el-radio-group>
        <el-checkbox
          v-model="viewControlObjects"
          @change="viewControlObjectsUpdated"
          >View Control Objects</el-checkbox
        >
      </el-col>
    </el-row>
    <el-select
      filterable
      placeholder
      style="width: 100%"
      v-model="$v.selection.$model"
      value-key="name"
      v-if="type === 'Task'"
      :class="{ error: checkValid && $v.selection.$invalid }"
    >
      <el-option
        v-for="task in tasks"
        :key="task.name"
        :label="task.name"
        :value="task"
      />
    </el-select>
    <el-select
      filterable
      placeholder
      style="width: 100%"
      v-model="$v.selection.$model"
      value-key="name"
      v-else
      :class="{ error: checkValid && $v.selection.$invalid }"
    >
      <el-option
        v-for="process in processes"
        :key="process.name"
        :label="process.name"
        :value="process"
      />
    </el-select>
    <div
      v-if="
        type === 'Process' &&
        selection &&
        selection.parameters &&
        selection.parameters.length > 0
      "
      style="margin-top: 20px"
    >
      <span style="font-size: 14px; font-weight: 500">Process Parameters</span>
      <el-form style="margin-top: 10px">
        <el-form-item
          v-for="({ Prompt, Value }, index) in selection.parameters"
          :key="index"
          :label="Prompt"
        >
          <el-input
            v-model="$v.selection.$model.parameters[index].Value"
            :placeholder="Value"
          />
        </el-form-item>
      </el-form>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button class="button" @click="handleSubmit">Add</el-button>
      <!-- <el-button class="cancel-button" @click="$emit('update:isVisible', false)">Close</el-button> -->
    </span>
  </el-dialog>
</template>

<script>
import { mapState } from "vuex";
import TaskService from "../../services/TaskService";
import JobService from "../../services/JobService";
import { required } from "vuelidate/lib/validators";
import Helper from "../../utilities/Helper";

/**
 * @vue-data {String} [type=null] - Type of the selected activity
 * @vue-data {String} [selection=null] - Selected activity
 * @vue-data {Array} [tasks=null] - List of available tasks
 * @vue-data {Array} [processes=null] - List of PA server processes
 * @vue-data {Boolean} [checkValid=false] - Flag that indicates whether to check for errors
 * @vue-computed {Helper} mapState - Maps Vuex state to computed properties
 */
export default {
  props: {
    isVisible: Boolean,
  },
  data() {
    return {
      type: "Task",
      selection: null,
      tasks: [],
      processes: [],
      processesHidden: [],
      checkValid: false,
      show: this.isVisible,
      viewControlObjects: true,
    };
  },
  watch: {
    type() {
      this.selection = null;
    },
    isVisible(value) {
      this.show = value;
    },
  },
  computed: {
    ...mapState(["environmentId", "instanceId"]),
  },
  methods: {
    handleError: Helper.handleError,
    /**
     * Shows or hides the control objects
     * @function
     */
    async viewControlObjectsUpdated() {
      [this.processes, this.processesHidden] = [
        this.processesHidden,
        this.processes,
      ];
    },
    /**
     * Fetches the lists of tasks and TurboIntegrator processes
     * @function
     */
    async getOptions() {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        this.tasks = [];
        const taskResponse = await TaskService.show({
          instanceId: this.instanceId,
        });
        taskResponse.data.tasks.forEach((task) =>
          this.tasks.push({
            id: task.id,
            name: task.name,
          })
        );
        this.processes = [];
        const processResponse = await JobService.getProcesses({
          instanceId: this.instanceId,
        });
        processResponse.data.processes.value.forEach((process) =>
          this.processes.push({
            name: process.Name,
            source: process.DataSource.Type,
            parameters: process.Parameters,
          })
        );
        this.processesHidden = this.processes.filter(
          (process) => process.name.charAt(0) !== "}"
        );
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    /**
     * Resets the modal
     * @function
     */
    resetModal(closeModal) {
      this.selection = null;
      this.checkValid = false;

      if (closeModal) {
        this.type = "Task";

        if (!this.viewControlObjects) {
          this.viewControlObjectsUpdated();
          this.viewControlObjects = true;
        }

        this.$emit("update:isVisible", false);
      }
    },
    /**
     * Submits the new activity information
     * @function
     */
    handleSubmit() {
      this.checkValid = true;
      if (this.$v.selection.$invalid) return;

      switch (this.type) {
        case "Task":
          this.$emit("activitySubmitted", {
            type: this.type,
            selection: this.selection.id,
          });
          break;
        case "Process":
          this.$emit("activitySubmitted", {
            type: this.type,
            selection: {
              name: this.selection.name,
              source: this.selection.source,
              parameters: this.selection.parameters,
            },
          });
          break;
      }

      this.checkValid = false;
      this.resetModal(false);
    },
  },
  mounted() {
    this.getOptions();
  },
  validations() {
    return {
      selection:
        this.type === "Task"
          ? {
              id: { required },
              name: { required },
            }
          : {
              name: { required },
              source: { required },
            },
    };
  },
};
</script>

<style scoped>
.error :deep(.el-input__inner) {
  border: 1px solid red;
  border-radius: 4px;
}
.button {
  background-color: #b3282d;
  margin-bottom: 15px;
  color: white;
  border-color: white;
}
.button:focus {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.button:hover {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.cancel-button {
  background-color: #7a7c80;
  color: white;
  border-color: white;
}
.cancel-button:focus {
  background-color: #606266;
  color: white;
  border-color: white;
  outline: none;
}
.cancel-button:hover {
  background-color: #606266;
  color: white;
  border-color: white;
  outline: none;
}
</style>
